import React, { useState, useEffect } from "react"

import QuillEditor, {
  BUILDER_V2_HEADER_LABELS,
  BUILDER_V2_TOOLBAR_OPTIONS
} from "../../../../components/shared/QuillEditor"
import Typography from "../../../../components/shared/Typography"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import BackgroundColorSelector from "../shared/BackgroundColorSelector"

import EditorFooter from "./shared/EditorFooter"

const FaqEditor = ({ section, practice, onSave, onCancel, onDelete, isEditing = false }) => {
  const { handleUpdateSectionPreview, clearPreview } = useWebsiteBuilderV2()
  const [formData, setFormData] = useState({
    headline: section?.headline || "Frequently Asked Questions",
    subheadline: section?.subheadline || "Find answers to common questions",
    body: section?.body || "",
    imageUrl: section?.imageUrl || "",
    settings: section?.settings || {
      faqs: [
        {
          question: "What should I expect during my first visit?",
          answer: "Your first visit will include a comprehensive consultation where we discuss your needs and goals."
        }
      ],
      layout: "accordion",
      showSearch: true,
      showBackgroundColor: false
    }
  })

  const [errors, setErrors] = useState({})

  // Get text color from settings for helper text
  const textColor = formData.settings?.textColor || "#1F2937"

  useEffect(() => {
    if (section) {
      setFormData({
        headline: section.headline || "Frequently Asked Questions",
        subheadline: section.subheadline || "Find answers to common questions",
        body: section.body || "",
        imageUrl: section.imageUrl || "",
        settings: {
          faqs: [
            {
              question: "What should I expect during my first visit?",
              answer:
                "Your first visit will include a comprehensive consultation where we discuss your needs and goals."
            }
          ],
          layout: "accordion",
          showSearch: true,
          showBackgroundColor: false,
          ...section.settings
        }
      })
    }
  }, [section, practice])

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value }
      if (errors[field]) {
        setErrors((prevErrors) => ({ ...prevErrors, [field]: null }))
      }
      if (section?.id && ["headline", "subheadline", "body", "imageUrl"].includes(field)) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...updated
        })
      }
      return updated
    })
  }

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => {
      const updatedSettings = { ...prev.settings, [key]: value }

      if (section?.id && ["faqs", "colorMode", "showBackgroundColor", "backgroundColor", "textColor"].includes(key)) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...prev,
          settings: updatedSettings
        })
      }

      return {
        ...prev,
        settings: updatedSettings
      }
    })
  }

  const addFaq = () => {
    const newFaq = { question: "", answer: "" }
    handleSettingsChange("faqs", [...(formData.settings.faqs || []), newFaq])
  }

  const removeFaq = (index) => {
    const updatedFaqs = formData.settings.faqs.filter((_, i) => i !== index)
    handleSettingsChange("faqs", updatedFaqs)
  }

  const updateFaq = (index, field, value) => {
    const updatedFaqs = formData.settings.faqs.map((faq, i) => (i === index ? { ...faq, [field]: value } : faq))
    handleSettingsChange("faqs", updatedFaqs)
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.headline.trim()) {
      newErrors.headline = "Section headline is required"
    }

    const faqs = formData.settings.faqs || []
    if (faqs.length === 0) {
      newErrors.faqs = "At least one FAQ is required"
    } else {
      const hasEmptyFaq = faqs.some((faq) => !faq.question.trim() || !faq.answer.trim())
      if (hasEmptyFaq) {
        newErrors.faqs = "All FAQ questions and answers must be filled out"
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e?.preventDefault?.()
    if (validateForm()) {
      onSave({
        layout: "faq",
        ...formData
      })
    }
  }

  const handleCancelClick = () => {
    clearPreview()
    onCancel()
  }

  return (
    <>
      {/* Form Content */}
      <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-8 pt-6">
        {/* Section Title */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Section title</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Add a short title to describe this section.</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative h-20 w-full shrink-0 rounded-lg bg-white">
              <input
                type="text"
                value={formData.headline}
                onChange={(e) => handleChange("headline", e.target.value)}
                placeholder="Frequently Asked Questions"
                className="box-border flex h-20 w-full flex-row content-stretch items-start justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
                required
              />
            </div>
            {errors.headline && (
              <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-red-500">
                <p className="block leading-[19.5px]">{errors.headline}</p>
              </div>
            )}
          </div>
        </div>

        {/* Section Subheadline */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Section subheadline</p>
            </div>
            <div
              className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal"
              style={{ color: textColor, opacity: 0.8 }}>
              <p className="block leading-[20px]">Add a subtitle to provide more context about this section.</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative h-20 w-full shrink-0 rounded-lg bg-white">
              <input
                type="text"
                value={formData.subheadline}
                onChange={(e) => handleChange("subheadline", e.target.value)}
                placeholder="Find answers to common questions"
                className="box-border flex h-20 w-full flex-row content-stretch items-start justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Section Text Color */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Section text color</p>
            </div>
            <div
              className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal"
              style={{ color: textColor, opacity: 0.8 }}>
              <p className="block leading-[20px]">Choose the text color for all content in this section.</p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <Typography variant="small" className="mb-1 font-medium text-gray-dark">
                Text Color
              </Typography>
              <input
                type="color"
                value={formData.settings.textColor || "#1F2937"}
                onChange={(e) => handleSettingsChange("textColor", e.target.value)}
                className="h-10 w-full rounded-md border border-gray-300 p-1"
              />
            </div>
          </div>
        </div>

        {/* Section Background Color */}
        <BackgroundColorSelector formData={formData} onSettingsChange={handleSettingsChange} sectionType="faq" />

        {/* FAQ Questions */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 px-0 pb-6 pt-0">
          {(formData.settings.faqs || []).map((faq, index) => (
            <div
              key={index}
              className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
              <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-start gap-0.5 p-0">
                <div className="relative min-h-px min-w-px shrink-0 grow basis-0 text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                  <p className="block leading-[24px]">Question {index + 1}</p>
                </div>
                <button
                  type="button"
                  onClick={() => removeFaq(index)}
                  className="relative shrink-0 text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-red-500 transition-colors hover:text-red-700">
                  <p className="block leading-[24px]">Delete</p>
                </button>
              </div>

              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
                {/* Question Input */}
                <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
                  <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-[#0b3954]">
                    <p className="block whitespace-pre leading-[20px]">Question</p>
                  </div>
                  <div className="relative w-full shrink-0 rounded-lg bg-white">
                    <input
                      type="text"
                      value={faq.question}
                      onChange={(e) => updateFaq(index, "question", e.target.value)}
                      placeholder="What is your question?"
                      className="relative box-border flex w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
                      required
                    />
                  </div>
                </div>

                {/* Answer Input */}
                <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
                  <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-[#0b3954]">
                    <p className="block whitespace-pre leading-[20px]">Answer</p>
                  </div>
                  <div className="relative w-full shrink-0 rounded-lg bg-white">
                    <QuillEditor
                      value={faq.answer}
                      onChange={(content) => updateFaq(index, "answer", content)}
                      toolbarOptions={BUILDER_V2_TOOLBAR_OPTIONS}
                      headerLabelOverrides={BUILDER_V2_HEADER_LABELS}
                      className="quill-editor-v2 min-h-[120px]"
                    />
                  </div>
                  <div
                    className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[12px] font-normal leading-[0]"
                    style={{ color: textColor, opacity: 0.7 }}>
                    <p className="block leading-[18px]">
                      Use the toolbar above to format your answer with bold, italic, lists, and more
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Add Another Question Link */}
          <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-between p-0">
            <button
              type="button"
              onClick={addFaq}
              className="relative min-h-px min-w-px shrink-0 grow basis-0 text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0d9da4] underline decoration-[#0d9da4] underline-offset-1 transition-colors hover:text-[#0b8a90]">
              <p className="block leading-[24px]">+ Add another question</p>
            </button>
          </div>
        </div>

        {errors.faqs && (
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-red-500">
              <p className="block leading-[19.5px]">{errors.faqs}</p>
            </div>
          </div>
        )}
      </div>

      <EditorFooter onDelete={onDelete} onCancel={handleCancelClick} onSave={handleSubmit} showDelete={isEditing} />
    </>
  )
}

export default FaqEditor
